import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Setup from "@/pages/Setup";
import Register from "@/pages/Register";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import PublicRequest from "@/pages/PublicRequest";
import Dashboard from "@/pages/Dashboard";
import CalendarPage from "@/pages/CalendarPage";
import Requests from "@/pages/Requests";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import Vehicles from "@/pages/Vehicles";
import VehicleDetail from "@/pages/VehicleDetail";
import Services from "@/pages/Services";
import Booking from "@/pages/Booking";
import Settings from "@/pages/Settings";
import Quotes from "@/pages/Quotes";
import Invoices from "@/pages/Invoices";
import Payments from "@/pages/Payments";
import Expenses from "@/pages/Expenses";
import Urssaf from "@/pages/Urssaf";
import Stats from "@/pages/Stats";
import Documents from "@/pages/Documents";
import Gallery from "@/pages/Gallery";
import Chat from "@/pages/Chat";
import Team from "@/pages/Team";
import SuperAdmin from "@/pages/SuperAdmin";
import Subscription from "@/pages/Subscription";
import SubscriptionSuccess from "@/pages/SubscriptionSuccess";
import Onboarding from "@/pages/Onboarding";
import Legal from "@/pages/Legal";
import ClientPortal from "@/pages/ClientPortal";
import Placeholder from "@/pages/Placeholder";
import Prospects from "@/pages/Prospects";
import Notifications from "@/pages/Notifications";
import Unpaid from "@/pages/Unpaid";
import Suppliers from "@/pages/Suppliers";
import GiftCards from "@/pages/GiftCards";
import RecurringInvoices from "@/pages/RecurringInvoices";
import MesEmails from "@/pages/MesEmails";
import MesSms from "@/pages/MesSms";
import AssistantIA from "@/pages/AssistantIA";
import Marketing from "@/pages/Marketing";
import Deplacements from "@/pages/Deplacements";
import Annuaire from "@/pages/Annuaire";
import Roue from "@/pages/Roue";
import Relances from "@/pages/Relances";
import Stock from "@/pages/Stock";
import Analytics from "@/pages/Analytics";
import Corbeille from "@/pages/Corbeille";
import Outils from "@/pages/Outils";
import Today from "@/pages/Today";
import Tasks from "@/pages/Tasks";
import Contracts from "@/pages/Contracts";
import RecurringAppointments from "@/pages/RecurringAppointments";
import Resources from "@/pages/Resources";
import Growth from "@/pages/Growth";
import CommTemplates from "@/pages/CommTemplates";
import BusinessProfilePage from "@/pages/BusinessProfilePage";
import QRCodes from "@/pages/QRCodes";
import FieldMode from "@/pages/FieldMode";
import Bons from "@/pages/Bons";
import Avoirs from "@/pages/Avoirs";
import NotificationSettings from "@/pages/NotificationSettings";
import DetailingTools from "@/pages/DetailingTools";
import ProductsUsedHistory from "@/pages/ProductsUsedHistory";
import ProductsReclass from "@/pages/ProductsReclass";
import Workshop from "@/pages/Workshop";
import ProtectionsTracking from "@/pages/ProtectionsTracking";
import Espace from "@/pages/Espace";
import { BusinessProfileProvider } from "@/context/BusinessProfileContext";
import { UnsavedChangesPrompt } from "@/lib/unsavedGuard";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
    </div>
  );
}

function SubscriptionGate() {
  return (
    <div className="max-w-lg mx-auto mt-10 bg-white border border-amber-200 rounded-2xl p-8 text-center" data-testid="subscription-gate">
      <h2 className="font-heading text-xl font-bold text-slate-900">Votre accès est en pause</h2>
      <p className="text-sm text-slate-500 mt-2">Votre essai ou abonnement a expiré. Vos données sont conservées en sécurité. Réactivez votre abonnement pour retrouver l'accès complet.</p>
      <a href="/abonnement" data-testid="gate-subscribe" className="inline-block mt-5 px-5 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">Voir mon abonnement</a>
    </div>
  );
}

function ProtectedBare({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role === "owner" && user.setup_completed === false) return <Navigate to="/onboarding" replace />;
  const gated = !user.subscription_active && !user.is_super_admin && user.company_plan !== "owner";
  if (gated && !loc.pathname.startsWith("/abonnement")) return <Layout><SubscriptionGate /></Layout>;
  return <Layout>{children}</Layout>;
}

function App() {
  return (
    <div className="App">
      <Toaster theme="light" position="top-right" richColors />
      <UnsavedChangesPrompt />
      <BrowserRouter>
        <AuthProvider>
          <BusinessProfileProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/setup" element={<Setup />} />
            <Route path="/creer-mon-compte" element={<Register />} />
            <Route path="/verifier-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/demande-rendez-vous" element={<PublicRequest />} />
            <Route path="/p/:slug" element={<PublicRequest />} />
            <Route path="/cgu" element={<Legal doc="cgu" />} />
            <Route path="/confidentialite" element={<Legal doc="confidentialite" />} />
            <Route path="/mentions-legales" element={<Legal doc="mentions" />} />
            <Route path="/mon-espace" element={<ClientPortal />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            <Route path="/calendar" element={<Protected><CalendarPage /></Protected>} />
            <Route path="/requests" element={<Protected><Requests /></Protected>} />
            <Route path="/clients" element={<Protected><Clients /></Protected>} />
            <Route path="/clients/:id" element={<Protected><ClientDetail /></Protected>} />
            <Route path="/vehicles" element={<Protected><Vehicles /></Protected>} />
            <Route path="/vehicles/:id" element={<Protected><VehicleDetail /></Protected>} />
            <Route path="/services" element={<Protected><Services /></Protected>} />
            <Route path="/page-reservation" element={<Protected><Booking /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/facturation-electronique" element={<Protected><Settings /></Protected>} />
            <Route path="/quotes" element={<Protected><Quotes /></Protected>} />
            <Route path="/invoices" element={<Protected><Invoices /></Protected>} />
            <Route path="/payments" element={<Protected><Payments /></Protected>} />
            <Route path="/expenses" element={<Protected><Expenses /></Protected>} />
            <Route path="/comptabilite" element={<Protected><Urssaf /></Protected>} />
            <Route path="/urssaf" element={<Navigate to="/comptabilite" replace />} />
            <Route path="/stats" element={<Protected><Stats /></Protected>} />
            <Route path="/documents" element={<Protected><Documents /></Protected>} />
            <Route path="/galerie" element={<Protected><Gallery /></Protected>} />
            <Route path="/prospects" element={<Protected><Prospects /></Protected>} />
            <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
            <Route path="/impayes" element={<Protected><Unpaid /></Protected>} />
            <Route path="/fournisseurs" element={<Protected><Suppliers /></Protected>} />
            <Route path="/cheques-cadeaux" element={<Protected><GiftCards /></Protected>} />
            <Route path="/factures-recurrentes" element={<Protected><RecurringInvoices /></Protected>} />
            <Route path="/mes-emails" element={<Protected><MesEmails /></Protected>} />
            <Route path="/mes-sms" element={<Protected><MesSms /></Protected>} />
            <Route path="/assistant-ia" element={<Protected><AssistantIA /></Protected>} />
            <Route path="/marketing" element={<Protected><Marketing /></Protected>} />
            <Route path="/frais-deplacement" element={<Protected><Deplacements /></Protected>} />
            <Route path="/annuaire" element={<Protected><Annuaire /></Protected>} />
            <Route path="/roue" element={<Protected><Roue /></Protected>} />
            <Route path="/relances" element={<Protected><Relances /></Protected>} />
            <Route path="/stock" element={<Protected><Stock /></Protected>} />
            <Route path="/analytics" element={<Protected><Analytics /></Protected>} />
            <Route path="/corbeille" element={<Protected><Corbeille /></Protected>} />
            <Route path="/outils" element={<Protected><Outils /></Protected>} />
            <Route path="/outils-detailing" element={<Protected><DetailingTools /></Protected>} />
            <Route path="/produits-utilises" element={<Protected><ProductsUsedHistory /></Protected>} />
            <Route path="/produits-a-reclasser" element={<Protected><ProductsReclass /></Protected>} />
            <Route path="/rdv/:id/atelier" element={<Protected><Workshop /></Protected>} />
            <Route path="/suivi-protections" element={<Protected><ProtectionsTracking /></Protected>} />
            <Route path="/espaces/:key" element={<Protected><Espace /></Protected>} />
            <Route path="/chat" element={<Protected><Chat /></Protected>} />
            <Route path="/today" element={<Protected><Today /></Protected>} />
            <Route path="/taches" element={<Protected><Tasks /></Protected>} />
            <Route path="/contrats" element={<Protected><Contracts /></Protected>} />
            <Route path="/interventions-recurrentes" element={<Protected><RecurringAppointments /></Protected>} />
            <Route path="/ressources" element={<Protected><Resources /></Protected>} />
            <Route path="/croissance" element={<Protected><Growth /></Protected>} />
            <Route path="/modeles-messages" element={<Protected><CommTemplates /></Protected>} />
            <Route path="/configuration" element={<Protected><BusinessProfilePage /></Protected>} />
            <Route path="/qr-codes" element={<Protected><QRCodes /></Protected>} />
            <Route path="/terrain" element={<Protected><FieldMode /></Protected>} />
            <Route path="/bons" element={<Protected><Bons /></Protected>} />
            <Route path="/avoirs" element={<Protected><Avoirs /></Protected>} />
            <Route path="/parametres/notifications" element={<Protected><NotificationSettings /></Protected>} />
            <Route path="/equipe" element={<Protected><Team /></Protected>} />
            <Route path="/super-admin" element={<Protected><SuperAdmin /></Protected>} />
            <Route path="/abonnement" element={<Protected><Subscription /></Protected>} />
            <Route path="/abonnement/success" element={<ProtectedBare><SubscriptionSuccess /></ProtectedBare>} />
            <Route path="/onboarding" element={<ProtectedBare><Onboarding /></ProtectedBare>} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </BusinessProfileProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
